import {
  PropsWithChildren,
  ReactNode,
  useEffect,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-block;
  cursor: pointer;
`;

const tooltipPositionStyles = {
  'top-left': `
    bottom: 125%;
    left: 0;
    transform: translateX(0);
  `,
  'top-center': `
    bottom: 125%;
    left: 50%;
    transform: translateX(-50%);
  `,
  'top-right': `
    bottom: 125%;
    right: 0;
    transform: translateX(0);
  `,
  'bottom-left': `
    top: 125%;
    left: 0;
    transform: translateX(0);
  `,
  'bottom-center': `
    top: 125%;
    left: 50%;
    transform: translateX(-50%);
  `,
  'bottom-right': `
    top: 125%;
    right: 0;
    transform: translateX(0);
  `
};

type TooltipPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

const TooltipContent = styled.div<{ show: boolean; position: TooltipPosition }>`
  visibility: ${props => (props.show ? 'visible' : 'hidden')};
  background-color: black;
  color: #fff;
  text-align: center;
  border-radius: 12px;
  padding: 10px;
  position: absolute;
  z-index: 1;
  opacity: ${props => (props.show ? 1 : 0)};
  transition: opacity 0.3s;

  ${({ position }) => position && tooltipPositionStyles[position]}
`;

interface TooltipProps extends PropsWithChildren {
  tooltipTrigger: ReactNode;
  position: TooltipPosition;
  tooltipContentStyle?: React.CSSProperties;
  tooltipWrapperStyle?: React.CSSProperties;
}

/**
 *
 * @param tooltipTrigger The element that will trigger the tooltip to show
 * @param children The content of the tooltip
 */
export const Tooltip = ({
  tooltipTrigger,
  position,
  children,
  tooltipContentStyle,
  tooltipWrapperStyle
}: TooltipProps) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      tooltipRef.current &&
      !tooltipRef.current.contains(event.target as Node)
    ) {
      setShowTooltip(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const TooltipTrigger = ({ children }: PropsWithChildren) => {
    return <div>{children}</div>;
  };

  return (
    <TooltipWrapper
      ref={tooltipRef}
      onClick={e => {
        e.stopPropagation();
        setShowTooltip(!showTooltip);
      }}
      style={tooltipWrapperStyle}
    >
      <TooltipTrigger>{tooltipTrigger}</TooltipTrigger>

      <TooltipContent
        position={position}
        style={tooltipContentStyle}
        show={showTooltip}
      >
        {children}
      </TooltipContent>
    </TooltipWrapper>
  );
};
